import { useState, useEffect, useRef } from 'react';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';

type DataSet = unknown;

type Section = {
  fields?: Array<{
    autofill: {
      expression: {
        variables: Array<{
          referenceType: string;
          referenceId: string;
        }>;
      } | null;
    } | null
  }>;
};

type Props = {
  sections: Section[];
};

const useExternalDataSets = ({ sections }: Props): Record<string, unknown> => {
  const [datasets, setDatasets] = useState<Record<string, DataSet>>({});
  const { getData: getDatasets } = useNoodleApi(tsClient.datasets.getDatasets);
  const isLoadingRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const neededSlugsSet = new Set<string>();
    sections.forEach((section) => {
      section.fields?.forEach((field) => {
        if (field.autofill?.expression?.variables) {
          field.autofill.expression.variables.forEach((variable) => {
            if (
              variable.referenceType === 'data-reference-slug'
              && !isLoadingRef.current.has(variable.referenceId)
            ) {
              neededSlugsSet.add(variable.referenceId);
            }
          });
        }
      });
    });

    const slugsToLoad = Array.from(neededSlugsSet);

    if (slugsToLoad.length === 0) {
      return;
    }

    // Mark the slug as being loaded ouside of loadTheseSlugs so that it's handled synchronously.
    slugsToLoad.forEach((slug) => {
      isLoadingRef.current.add(slug);
    });

    const loadTheseSlugs = async (): Promise<void> => {
      const { data } = await getDatasets({ slugs: slugsToLoad });
      if (data) {
        setDatasets(prevState => ({
          ...prevState,
          ...data.reduce((acc, val) => {
            let thisDatasetData = val.data;
            try {
              thisDatasetData = (typeof val.data === 'string')
                ? JSON.parse(val.data)
                : val.data;
            } catch (_error) { /* ignore */ }
            return {
              ...acc,
              [val.slug]: thisDatasetData,
            };
          }, {}),
        }));
      }
    };

    loadTheseSlugs();
  }, [sections, getDatasets]);

  return datasets;
};

export default useExternalDataSets;
